'use client';

import { useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';

import { benefitsId, collaborationId, howItWorksId } from '@/utils/sectionIds';

import type { FooterConfigType } from '@/components/organisms/Footer/Footer.types';

import { getPromoCodesModalConfigRO } from './promo-codes-modal';

import { env } from '@/env.mjs';

export const useFooterRO = (): FooterConfigType => {
    const [promoCodesModalVisible, setPromoCodesModalVisible] = useState(false);

    const location = usePathname();

    useEffect(() => {
        setPromoCodesModalVisible(false);
    }, [location]);

    return {
        links: [
            {
                name: 'PayPo',
                logoIsVisible: true,
                partnersLogo: [
                    {
                        href: 'https://romania.payu.com/',
                        width: 90,
                        height: 32,
                        src: '/images/logos/payu-ro.svg',
                        alt: 'PayU',
                        className: 'block lg:mb-6',
                    },
                    {
                        href: 'https://netopia-payments.com/',
                        width: 84,
                        height: 32,
                        src: '/images/logos/netopia-logo.svg',
                        alt: 'Netopia Payments',
                        className: 'block lg:mb-6',
                    },
                    {
                        href: 'https://www.euplatesc.ro/',
                        width: 101,
                        height: 26,
                        src: '/images/logos/euplatesc-logo.svg',
                        alt: 'Euplatesc',
                    },
                ],
            },
            {
                name: 'Clienți',
                menu: {
                    lead: {
                        href: '/',
                    },
                    list: [
                        {
                            name: 'Cum funcționează?',
                            relatedSectionId: howItWorksId,
                            href: `/#${howItWorksId}`,
                            'data-test-id': 'footer-how-it-works-link',
                        },
                        {
                            name: 'Beneficii',
                            relatedSectionId: benefitsId,
                            href: `/#${benefitsId}`,
                            'data-test-id': 'footer-benefits-link',
                        },
                        {
                            name: 'Magazine partenere',
                            href: `/p/magazinepartnere`,
                            'data-test-id': 'footer-available-shops-page-link',
                        },
                        {
                            name: 'Contact',
                            href: `/p/contact`,
                            'data-test-id': 'footer-contact-page-link',
                        },
                        {
                            name: 'Accesibilitate',
                            href: `/p/accesibilitate`,
                            'data-test-id': 'footer-accessibility-page-link',
                        },
                        {
                            name: 'Retragere din contract',
                            href: `/p/retragere`,
                            'data-test-id': 'footer-renouncement-page-link',
                        },
                        {
                            name: 'Coduri promoționale',
                            onClick: () => setPromoCodesModalVisible(true),
                            'data-test-id': 'footer-promo-codes-link',
                        },
                    ],
                },
            },
            {
                name: 'Comercianți',
                menu: {
                    lead: {
                        href: '/p/comercianti/',
                    },
                    list: [
                        {
                            name: 'Beneficii',
                            relatedSectionId: benefitsId,
                            href: `/p/comercianti/#${benefitsId}`,
                            'data-test-id': 'footer-partners-why-worth-ref',
                        },
                        {
                            name: 'Cum funcționează?',
                            relatedSectionId: howItWorksId,
                            href: `/p/comercianti/#${howItWorksId}`,
                            'data-test-id': 'footer-partners-how-it-works-link',
                        },
                        {
                            name: 'Integrare',
                            href: `/p/comercianti/integrare`,
                            'data-test-id': 'footer-integrare-link',
                        },
                        {
                            name: 'Începe colaborarea',
                            relatedSectionId: collaborationId,
                            href: `/p/comercianti/#${collaborationId}`,
                            'data-test-id': 'footer-partners-integration-link',
                        },
                    ],
                },
            },
            {
                name: 'Informații legale',
                menu: {
                    list: [
                        {
                            name: 'Termeni și Condiții Servicii PayPo',
                            href: `${env.NEXT_PUBLIC_STATIC_WWW_URL_RO}/documents/Termenii_si_Conditiile_serviciilor_PayPo.pdf`,
                            target: '_blank',
                            'data-test-id':
                                'footer-termenii_si_conditiile_serviciilor_paypo-link',
                        },
                        {
                            name: 'Condiții de Utilizare PayPo',
                            href: `${env.NEXT_PUBLIC_STATIC_WWW_URL_RO}/documents/Conditii_de_utilizare_PayPo.pdf`,
                            target: '_blank',
                            'data-test-id':
                                'footer-conditii_de_utilizare_paypo-link',
                        },
                        {
                            name: 'Termenii și Condițiile Aplicației Mobile PayPo',
                            href: `${env.NEXT_PUBLIC_STATIC_WWW_URL_RO}/documents/termenii-si-conditiile-aplicatiei-mobile-paypo.pdf`,
                            target: '_blank',
                            'data-test-id':
                                'footer-termenii-si-conditiile-aplicatiei-mobile-paypo',
                        },
                        {
                            name: 'Informații cu privire la prelucrarea datelor cu caracter personal',
                            href: `${env.NEXT_PUBLIC_STATIC_WWW_URL_RO}/documents/Informare_cu_privire_la_prelucrarea_datelor.pdf`,
                            target: '_blank',
                            'data-test-id':
                                'footer-informare_cu_privire_la_prelucrarea_datelor-link',
                        },
                        {
                            name: 'Politica de confidențialitate și cookie-uri',
                            href: '/p/politica-de-confidentialitate-si-cookie-uri',
                            'data-test-id':
                                'footer-politica-de-confidentialitate-si-cookie-uri-link',
                        },
                        {
                            name: 'Informare prelucrare date în Biroul de credite',
                            href: `${env.NEXT_PUBLIC_STATIC_WWW_URL_RO}/documents/Informare_prelucrare_date_Biroul_de_Credit.pdf`,
                            target: '_blank',
                            'data-test-id':
                                'footer-informare_prelucrare_date_biroul_de_credit-link',
                        },
                        {
                            name: 'Acord pentru solicitarea veniturilor persoanelor fizice',
                            href: `${env.NEXT_PUBLIC_STATIC_WWW_URL_RO}/documents/Acord_pentru_solicitarea_veniturilor_persoanelor_fizice.pdf`,
                            target: '_blank',
                            'data-test-id':
                                'footer-acord_pentru_solicitarea_veniturilor_persoanelor_fizice-link',
                        },
                        {
                            name: 'Alte regulamente',
                            href: `/p/alteregulamente`,
                            'data-test-id': 'footer-alte-regulamente-link',
                        },
                        {
                            name: 'Arhiva de Termeni și Condiții',
                            href: `/p/arhiva-de-termeni-si-conditii`,
                            'data-test-id':
                                'footer-arhiva-de-termeni-si-conditii-link',
                        },
                        {
                            name: 'Centrul de gestionare a cookie-urilor',
                            onClick: () => {
                                window.Cookiebot?.renew();
                            },
                            'data-test-id':
                                'footer-cookies-management-center-link',
                        },
                    ],
                },
            },
        ],
        footerInfoLinks: [
            {
                href: 'https://reclamatiisal.anpc.ro/',
                height: 44,
                width: 200,
                alt: 'ANCP-1',
                src: '/images/icons/ANCP-1.svg',
            },
        ],
        promoCodesModal: {
            isVisible: promoCodesModalVisible,
            config: getPromoCodesModalConfigRO(() =>
                setPromoCodesModalVisible(false),
            ),
        },
    };
};
